import { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../hooks/useProjects';
import { usePostHog } from '@posthog/react';
import DashboardLayout from '../components/layout/DashboardLayout';
import ProjectList from '../components/projects/ProjectList';
import NewProjectModal from '../components/projects/NewProjectModal';
import { Plus, Search, RefreshCw } from 'lucide-react';

export default function Dashboard() {
  const { userData, currentUser } = useAuth();
  const { projects, loading, createProject, updateProject, deleteProject } = useProjects();
  const posthog = usePostHog();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const handleCreateProject = async (projectData) => {
    const result = await createProject(projectData);
    if (result.success && currentUser) {
      posthog?.capture('project_created', { project_name: projectData.name });
    }
    return result;
  };

  const handleEditProject = async (projectData) => {
    if (editingProject) {
      const result = await updateProject(editingProject.id, projectData);
      setEditingProject(null);
      return result;
    }
  };

  const handleDeleteProject = async (projectId) => {
    await deleteProject(projectId);
  };

  const openEditModal = (project) => {
    setEditingProject(project);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProject(null);
  };

  // Filter projects based on search query
  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) return projects;

    const query = searchQuery.toLowerCase();
    return projects.filter(project =>
      project.name.toLowerCase().includes(query) ||
      (project.description && project.description.toLowerCase().includes(query))
    );
  }, [projects, searchQuery]);

  // Calculate total stats
  const totalDocuments = projects.reduce((sum, p) => sum + (p.documentCount || 0), 0);
  const totalPages = projects.reduce((sum, p) => sum + (p.pageCount || 0), 0);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Projects
            </h1>
          </div>

          {/* Search */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 pl-10 pr-4 py-2 bg-white dark:bg-dark-bg border border-gray-300 dark:border-dark-border rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
              />
            </div>

            <button
              onClick={() => window.location.reload()}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>

            <button
              onClick={() => {
                setEditingProject(null);
                setIsModalOpen(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition font-medium"
            >
              <Plus className="w-5 h-5" />
              New Project
            </button>
          </div>
        </div>

        {/* Summary Line */}
        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {projects.length} {projects.length === 1 ? 'project' : 'projects'}
            {totalDocuments > 0 && (
              <>
                {' • '}
                {totalDocuments} {totalDocuments === 1 ? 'file' : 'files'}
              </>
            )}
            {totalPages > 0 && (
              <>
                {' • '}
                {totalPages} {totalPages === 1 ? 'page' : 'pages'}
              </>
            )}
          </p>
        </div>

        {/* Projects List */}
        <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
          <ProjectList
            projects={filteredProjects}
            loading={loading}
            onDelete={handleDeleteProject}
            onEdit={openEditModal}
            onCreateNew={() => setIsModalOpen(true)}
          />
        </div>

        {/* No Results */}
        {searchQuery && filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              No projects found matching "{searchQuery}"
            </p>
          </div>
        )}
      </div>

      {/* New/Edit Project Modal */}
      <NewProjectModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={editingProject ? handleEditProject : handleCreateProject}
        editProject={editingProject}
      />
    </DashboardLayout>
  );
}
